/**
 * Single source of truth for the user's shielded balance.
 *
 * Why a context: Wallet polls every 90 s, but Send and Swap need to
 * read the latest known balance the moment the user opens those tabs
 * (to gate the submit button against an impossible amount). Without
 * a shared store each screen would either re-poll on mount (wasteful)
 * or operate on stale local state (incorrect). The provider owns the
 * polling timer; consumers just read.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useShieldedKeys } from "./keys-context";
import {
  pollShieldedBalance,
  readUnspentNotes,
  clearAllPendingSpends,
  forceFreshSync,
} from "./balance";
import { EMPTY_BALANCE, type ShieldedBalance } from "./balance";
import { recordIncomingNotes } from "../lib/incoming-notes";

interface ShieldedBalanceApi {
  balance: ShieldedBalance;
  /** True between starting a poll and getting a response. */
  loading: boolean;
  error: string | null;
  /** Force a refresh (e.g. after a send broadcasts). Returns when settled. */
  refresh: () => Promise<void>;
  /**
   * Full reset of locally-cached shielded state, rebuilt from on-chain
   * truth. Handles two failure modes in one action:
   *
   *   1. The user has pending-spent markers from a relay that accepted
   *      a tx but never propagated it (silent drop). We clear the marks
   *      and re-sync; the scanner confirms the notes are still unspent.
   *   2. The user has empty local `unspentNotes` because an older build
   *      of the wallet permanently deleted them on optimistic broadcast.
   *      The pending-mark clear is a no-op there, but `forceFreshSync`
   *      rebuilds the unspent set from the chain so the funds reappear.
   *
   * Always safe to call: if local state was already correct, the scanner
   * returns the same notes and netBalanceZats stays put.
   */
  recoverPendingSpends: () => Promise<{
    cleared: number;
    netBalanceZats: number;
  }>;
}

const Ctx = createContext<ShieldedBalanceApi | null>(null);

const POLL_INTERVAL_MS = 90_000;

export function ShieldedBalanceProvider({ children }: { children: ReactNode }) {
  const { keys } = useShieldedKeys();
  const [balance, setBalance] = useState<ShieldedBalance>(EMPTY_BALANCE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Hold the latest UFVK in a ref so refresh() works without dependency
  // gymnastics when the consumer calls it from inside an async chain.
  const ufvkRef = useRef<string | null>(null);
  // Epoch counter bumped on every keys change. In-flight polls compare
  // against the snapshot they captured at start; if it doesn't match
  // when the response lands we bail without writing state. Stops a
  // reset-wallet race where the previous UFVK's notes would otherwise
  // pollute the new wallet's incoming-notes log.
  const epochRef = useRef(0);

  const refresh = useCallback(async () => {
    const ufvk = ufvkRef.current;
    if (!ufvk) return;
    const epoch = epochRef.current;
    setLoading(true);
    try {
      const b = await pollShieldedBalance(ufvk, (newNotes) => {
        // Bail if the wallet was reset / swapped underneath us — the
        // notes we just decrypted belong to the OLD UFVK and must not
        // be written into the new wallet's persistent log.
        if (epoch !== epochRef.current) return;
        recordIncomingNotes(
          newNotes.map((n) => ({
            nullifier: n.nullifier,
            txid: n.txid,
            value_zats: n.value_zats,
            height: n.height,
            memo: n.memo,
          })),
        );
      });
      if (epoch !== epochRef.current) return;
      setBalance(b);
      setError(null);
    } catch (e) {
      if (epoch !== epochRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (epoch === epochRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!keys) {
      // Bump epoch so any in-flight poll bails on resolve.
      epochRef.current += 1;
      ufvkRef.current = null;
      setBalance(EMPTY_BALANCE);
      return;
    }
    epochRef.current += 1;
    ufvkRef.current = keys.ufvk;
    // One-shot backfill: if the scanner has already discovered notes
    // before our incoming-history callback was wired (i.e. the wallet
    // was loaded under a previous build), pull whatever is in the
    // persisted unspent set into the history log. recordIncomingNotes
    // dedupes on nullifier so this is idempotent.
    void (async () => {
      try {
        const unspent = await readUnspentNotes(keys.ufvk);
        if (unspent.length > 0) {
          recordIncomingNotes(
            unspent.map((n) => ({
              nullifier: n.nullifier,
              txid: n.txid,
              value_zats: n.value_zats,
              height: n.height,
              memo: n.memo,
            })),
          );
        }
      } catch {
        // best-effort; live polls will pick up future notes anyway
      }
    })();
    void refresh();
    const t = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [keys, refresh]);

  const recoverPendingSpends = useCallback(async () => {
    const ufvk = ufvkRef.current;
    if (!ufvk) return { cleared: 0, netBalanceZats: balance.netBalanceZats };
    setLoading(true);
    try {
      // Step 1: clear pending marks (no-op for users on the very old
      // build where notes were deleted outright, useful for the new
      // path where they were just marked).
      const cleared = await clearAllPendingSpends(ufvk);
      // Step 2: rebuild the unspent set from on-chain. This is the
      // path that fixes users with empty local state — the scanner
      // re-discovers every still-unspent note from COLD_START_FROM_HEIGHT.
      const fresh = await forceFreshSync(ufvk);
      setBalance({
        netBalanceZats: fresh.netBalanceZats,
        unspentNoteCount: fresh.notes.length,
        anchorHeight: fresh.anchorHeight,
        lastSyncedHeight: fresh.lastSyncedHeight,
      });
      setError(null);
      return {
        cleared: cleared.cleared,
        netBalanceZats: fresh.netBalanceZats,
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      return { cleared: 0, netBalanceZats: balance.netBalanceZats };
    } finally {
      setLoading(false);
    }
  }, [balance.netBalanceZats]);

  const api = useMemo<ShieldedBalanceApi>(
    () => ({ balance, loading, error, refresh, recoverPendingSpends }),
    [balance, loading, error, refresh, recoverPendingSpends],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useShieldedBalance(): ShieldedBalanceApi {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error(
      "useShieldedBalance must be used inside <ShieldedBalanceProvider>",
    );
  }
  return v;
}
